import { ExternalLinkIcon, FolderIcon, SearchIcon } from 'lucide-react'
import {
  type LakeSearchFoldersByNameArgs,
  type LakeSearchFoldersByNameResult,
  splitFolderPath,
} from '@/features/agent/tools/lake-search-folders-by-name'
import { useToolCardOpen } from '@/features/agent/tools/useToolCardOpen'
import { cn } from '@/utils/cn'
import { HIT_META_GROUP_CLASS, HIT_NAME_ROOM } from './_shared/hit-row'
import { ToolCallCard, ToolCallHeader, type ToolCallStatus } from './_shared/ToolCallHeader'

export interface LakeSearchFoldersByNameViewProps {
  args: LakeSearchFoldersByNameArgs
  parsed: LakeSearchFoldersByNameResult | null
  running: boolean
  errored: boolean
  /** Open a folder hit. Receives the `label`/`path` split of the hit's path.
   *  The connected tool routes to the lake page; pure stories pass a no-op. */
  onOpenFolder?: (split: { label: string; path: string }) => void
  /** Force the card expanded even with no body. Defaults to false; the card
   *  auto-expands when it has results and stays collapsed for empty or failed
   *  calls. Stories pass true to render the expanded body. */
  defaultOpen?: boolean
}

/** Pure presentation for a `lake_search_folders_by_name` tool-result card. */
export function LakeSearchFoldersByNameView({
  args,
  parsed,
  running,
  errored,
  onOpenFolder,
  defaultOpen = false,
}: LakeSearchFoldersByNameViewProps) {
  const hits = parsed?.folders ?? []
  const total = parsed?.total ?? hits.length
  const hasBody = !running && (errored || hits.length > 0)
  const { open, onToggle } = useToolCardOpen(hasBody, defaultOpen, errored)

  const headerStatus: ToolCallStatus = running ? 'running' : errored ? 'error' : 'complete'

  const label = running
    ? 'Looking up folder for'
    : errored
      ? 'Failed —'
      : total === 0
        ? 'No folder match for'
        : `${total} folder match${total === 1 ? '' : 'es'} for`

  const description = (
    <>
      {label}{' '}
      <code className="ml-0.5 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground">
        "{args.name}"
      </code>
    </>
  )

  return (
    <ToolCallCard>
      <ToolCallHeader
        status={headerStatus}
        icon={SearchIcon}
        toolName="lake_search_folders_by_name"
        description={description}
        expandable={hasBody}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="flex flex-col gap-2 border-t border-border px-3 py-2">
          {parsed?.error && (
            <p className="whitespace-pre-wrap text-xs text-muted-foreground">{parsed.error}</p>
          )}
          {hits.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {hits.map((hit, i) => {
                const split = splitFolderPath(hit.path)
                const canOpen = Boolean(split)
                const body = (
                  <div className="flex items-start gap-2">
                    <FolderIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                    <div className="flex min-w-0 flex-col gap-1 max-md:flex-1">
                      <div className="flex flex-wrap items-center gap-1.5 text-xs">
                        <span
                          className={cn(
                            'truncate font-medium',
                            hit.label ? HIT_NAME_ROOM.icon : HIT_NAME_ROOM.iconAndCount,
                            canOpen && 'text-primary underline-offset-2 group-hover:underline'
                          )}
                        >
                          {hit.name}
                        </span>
                        {canOpen && (
                          <ExternalLinkIcon className="size-3 shrink-0 text-muted-foreground" />
                        )}
                        <span className={HIT_META_GROUP_CLASS}>
                          {hit.label && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                              {hit.label}
                            </span>
                          )}
                          <span className="ml-auto rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                            {hit.document_count} {hit.document_count === 1 ? 'file' : 'files'}
                          </span>
                        </span>
                      </div>
                      <p className="truncate text-[11px] text-muted-foreground/80">{hit.path}</p>
                    </div>
                  </div>
                )
                return (
                  <li key={hit.path || i}>
                    {canOpen && split ? (
                      <button
                        type="button"
                        onClick={() => onOpenFolder?.(split)}
                        className="group w-full cursor-pointer rounded-md border border-border px-2.5 py-2 text-left transition-colors hover:border-primary hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="rounded-md border border-border px-2.5 py-2">{body}</div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </ToolCallCard>
  )
}
